import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { User, Building2, Heart, Save, Loader2 } from "lucide-react";
import { COUNTRIES_PT } from "./profileConstants";
import { Section, Grid, Input, DateInput, Textarea, Select } from "./profile/fields";
import { ProfilePhotoSection } from "./profile/ProfilePhotoSection";
import { MbtiPicker } from "./profile/MbtiPicker";
import { ViaPicker } from "./profile/ViaPicker";
import { PrivacySections } from "./profile/PrivacySections";
import { BusinessCardSection } from "./profile/BusinessCardSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: "", phone: "", address: "", postal_code: "", city: "", country: "",
  nationality: "", birthplace: "", birth_date: "",
  company: "", role: "", professional_email: "", linkedin: "",
  billing_nif: "", billing_company_nif: "", billing_address: "",
  ideal_pessoal: "", alianca_amor: "", alianca_amor_date: "", alianca_amor_place: "",
  mbti: "", via_values: [],
  hide_from_directory: false, hide_email: false, hide_phone: false,
  hide_mbti: false, hide_via_values: false, hide_ideal_pessoal: false, hide_alianca_amor: false,
  hide_from_community_page: false,
};

const FIELD_LABELS = {
  ideal_pessoal: "Ideal Pessoal", alianca_amor: "Aliança de Amor",
  alianca_amor_place: "Local da Aliança", name: "Nome", phone: "Telemóvel",
  professional_email: "Email profissional",
};

// Converts FastAPI/Pydantic errors (detail can be a string OR an array of
// validation objects) into a readable PT message.
const friendlyApiError = (e) => {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail.length) {
    const d = detail[0];
    const field = FIELD_LABELS[d?.loc?.[d.loc.length - 1]] || d?.loc?.[d.loc.length - 1] || "campo";
    if (d?.type === "string_too_long") {
      return `O texto em "${field}" é demasiado longo (máx. ${d?.ctx?.max_length} caracteres).`;
    }
    return `Valor inválido em "${field}".`;
  }
  return "Erro a guardar.";
};

export default function MemberProfile() {
  const [form, setForm] = useState(EMPTY);
  const [picture, setPicture] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/members/me/profile`, { withCredentials: true });
        const d = r.data || {};
        setForm({ ...EMPTY, ...Object.fromEntries(Object.entries(d).filter(([, v]) => v != null)) });
        setPicture(d.picture || "");
      } catch (e) {
        toast.error("Erro a carregar perfil.");
      } finally { setLoading(false); }
    })();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleVia = (val) => {
    const has = form.via_values.includes(val);
    if (has) set("via_values", form.via_values.filter((v) => v !== val));
    else if (form.via_values.length < 5) set("via_values", [...form.via_values, val]);
    else toast.error("Máximo 5 valores VIA-ME.");
  };

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      await axios.patch(`${API}/members/me/profile`, form, { withCredentials: true });
      toast.success("Perfil atualizado.");
    } catch (e) {
      toast.error(friendlyApiError(e));
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="container-page py-20 text-center text-[hsl(var(--muted-foreground))]" data-testid="profile-loading">
        <Loader2 className="h-6 w-6 animate-spin mx-auto" />
        <p className="mt-3 text-sm">A carregar perfil…</p>
      </div>
    );
  }

  return (
    <section className="container-page py-12 lg:py-16" data-testid="member-profile-page">
      <div className="max-w-4xl">
        <div className="overline">Perfil pessoal</div>
        <h1 className="display-heading text-4xl sm:text-5xl mt-3">O meu perfil.</h1>
        <span className="accent-rule mt-6 block" />
        <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))] max-w-2xl">
          Os seus dados ficam guardados em segurança. Pode escolher quais partilhar no Diretório de Sócios na secção de privacidade.
        </p>

        <form onSubmit={save} className="mt-12 space-y-12">
          <ProfilePhotoSection initialPicture={picture} name={form.name} />

          {/* Identidade & Contactos */}
          <Section icon={User} title="Identidade & Contactos">
            <Grid>
              <Input label="Nome completo" value={form.name} onChange={(v) => set("name", v)} testid="prof-name" required />
              <Input label="Telemóvel" value={form.phone} onChange={(v) => set("phone", v)} testid="prof-phone" />
              <Input label="NIF" value={form.billing_nif} onChange={(v) => set("billing_nif", v)} testid="prof-nif" />
              <Input label="Nacionalidade" value={form.nationality} onChange={(v) => set("nationality", v)} testid="prof-nationality" placeholder="Portuguesa" />
              <Input label="Naturalidade" value={form.birthplace} onChange={(v) => set("birthplace", v)} testid="prof-birthplace" placeholder="Lisboa" />
              <DateInput
                label="Data de nascimento"
                hint="Usaremos para enviar uma mensagem de parabéns."
                value={form.birth_date}
                onChange={(v) => set("birth_date", v)}
                testid="prof-birth-date"
              />
              <Select label="País" value={form.country} onChange={(v) => set("country", v)} testid="prof-country" options={["", ...COUNTRIES_PT]} />
            </Grid>
            <Grid cols={3}>
              <Input className="sm:col-span-2" label="Morada" value={form.address} onChange={(v) => set("address", v)} testid="prof-address" />
              <Input label="Código Postal" value={form.postal_code} onChange={(v) => set("postal_code", v)} testid="prof-postal" placeholder="1000-000" />
            </Grid>
            <div>
              <Input label="Localidade" value={form.city} onChange={(v) => set("city", v)} testid="prof-city" />
            </div>
          </Section>

          {/* Empresa */}
          <Section icon={Building2} title="Empresa (opcional)">
            <Grid>
              <Input label="Empresa" value={form.company} onChange={(v) => set("company", v)} testid="prof-company" />
              <Input label="Função" value={form.role} onChange={(v) => set("role", v)} testid="prof-role" placeholder="CEO, Diretor, ..." />
              <Input
                label="Email profissional"
                value={form.professional_email}
                onChange={(v) => set("professional_email", v)}
                testid="prof-professional-email"
                placeholder="nome@empresa.pt"
                hint="Se preenchido, é este o email mostrado aos outros sócios no Diretório (o email de login fica reservado ao acesso e comunicações da CIEES)."
              />
              <Input
                label="LinkedIn"
                value={form.linkedin}
                onChange={(v) => set("linkedin", v)}
                testid="prof-linkedin"
                placeholder="https://linkedin.com/in/o-seu-nome (ou só o nome de utilizador)"
                hint="Aparece como botão no seu cartão do Diretório."
              />
              <Input label="NIF da empresa" value={form.billing_company_nif} onChange={(v) => set("billing_company_nif", v)} testid="prof-cnif" />
              <Input label="Morada fiscal (faturação)" value={form.billing_address} onChange={(v) => set("billing_address", v)} testid="prof-faddr" />
            </Grid>
          </Section>

          {/* Identidade Schoenstattiana */}
          <Section icon={Heart} title="Identidade Schoenstattiana">
            <Textarea
              label="Ideal Pessoal"
              hint="Frase ou texto que sintetiza o seu ideal de vida."
              value={form.ideal_pessoal}
              onChange={(v) => set("ideal_pessoal", v)}
              testid="prof-ideal"
              rows={3}
            />
            <Textarea label="Aliança de Amor"
              hint="Texto estritamente privado — apenas visível para si. Nunca é partilhado no Diretório nem com a administração. Para aparecer no Diretório e nas estatísticas, preencha a data e o local abaixo."
              value={form.alianca_amor}
              onChange={(v) => set("alianca_amor", v)}
              testid="prof-alianca"
              rows={4} />
            <Grid>
              <DateInput
                label="Data da Aliança de Amor"
                value={form.alianca_amor_date}
                onChange={(v) => set("alianca_amor_date", v)}
                testid="prof-alianca-date"
                hint="Quando selou a Aliança de Amor."
              />
              <Input
                label="Local da Aliança"
                value={form.alianca_amor_place}
                onChange={(v) => set("alianca_amor_place", v)}
                testid="prof-alianca-place"
                placeholder="Ex.: Bellavista, Schoenstatt · Lisboa"
                hint="Santuário ou local onde foi selada."
              />
            </Grid>

            <MbtiPicker value={form.mbti} onChange={(v) => set("mbti", v)} />

            <ViaPicker values={form.via_values} onToggle={toggleVia} />
          </Section>

          <BusinessCardSection />

          <PrivacySections form={form} set={set} />

          <div className="flex justify-end pt-4 border-t border-[hsl(var(--border))]">
            <button type="submit" disabled={saving}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[hsl(var(--primary))] text-white text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#093630] transition-colors disabled:opacity-60"
              data-testid="profile-save">
              <Save className="h-4 w-4" /> {saving ? "A guardar…" : "Guardar perfil"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
